// Trailing-edge debounce. Calls within `ms` of each other coalesce into one
// invocation with the latest args. Used by the synced store to batch IndexedDB
// flushes per plan/02 §Write strategy ("debounced ~800 ms background flush").

export interface Debounced<A extends unknown[]> {
  (...args: A): void;
  cancel: () => void;
  flush: () => void;
}

export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): Debounced<A> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: A | null = null;

  const debounced = ((...args: A) => {
    lastArgs = args;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      const pending = lastArgs;
      timer = null;
      lastArgs = null;
      if (pending) fn(...pending);
    }, ms);
  }) as Debounced<A>;

  debounced.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    lastArgs = null;
  };

  debounced.flush = () => {
    if (timer && lastArgs) {
      clearTimeout(timer);
      const pending = lastArgs;
      timer = null;
      lastArgs = null;
      fn(...pending);
    }
  };

  return debounced;
}
